import { Grid, TextField, Typography } from '@material-ui/core';
import { FileUploadDownloadArea } from 'libs/feature-documents/src/documents/components/documentForms/common/fileUploadDownloadArea';
import { Button, DateField, FileUpload } from '@roc/ui';
import { _StateLicenseUpload } from '@roc/feature-one-tool';
import { useBaseStore } from '@roc/feature-app-core';
import { useLoanStore } from '@roc/feature-loans';
import { observer } from 'mobx-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { format, isValid } from 'date-fns';
import { TodoItem } from '../utils/loanDetailsTypes';
import { StateLicenseTaskService } from '../services/stateLicenseTaskService';
import { Alert, AlertTitle } from '@material-ui/lab';
import { isBlank } from '@roc/feature-utils';

interface Props {
  todo: TodoItem;
  onSubmitted?: () => void;
  existingFileName?: string;
  onDownloadExisting?: () => void;
  initialExpirationDate?: any;
}

const ORIGINATOR_STATE_LICENSE = 'Originator State License';

const typeLabel = (type: string) => (type === 'ORIGINATOR' ? 'Originator' : 'Company');

const LockedField = ({ label, value }: { label: string; value?: string }) => (
  <Grid item xs={12}>
    <TextField
      label={label}
      value={value ?? ''}
      disabled
      fullWidth
      variant="outlined"
      InputProps={{ readOnly: true }}
    />
  </Grid>
);

export const StateLicenseTaskForm = observer(({ todo, onSubmitted, existingFileName, onDownloadExisting, initialExpirationDate }: Props) => {
  const { globalStore } = useBaseStore();
  const { loanStore } = useLoanStore();
  const service = useMemo(() => new StateLicenseTaskService(), []);
  const taskId = todo?.document?.taskId;

  const loanDetails = loanStore.loanDetails;
  const type = todo?.document?.taskName === ORIGINATOR_STATE_LICENSE ? 'ORIGINATOR' : 'COMPANY';
  const state = loanDetails?.properties?.[0]?.state;

  // The originator name isn't reliably present on the frontend loanDetails here,
  // so fetch it from the backend (derived authoritatively from the loan).
  const [originator, setOriginator] = useState<string>('');
  useEffect(() => {
    service
      .getStateLicenseOriginator(taskId)
      .then(response => setOriginator(response?.data?.data ?? ''))
      .catch(() => setOriginator(''));
  }, [taskId]);

  const [expirationDate, setExpirationDate] = useState<any>(initialExpirationDate ?? null);
  const [expirationError, setExpirationError] = useState<string | null>(null);
  const [uploadItems, setUploadItems] = useState<FileUpload[]>([]);

  const lenderId = loanDetails?.lenderId;
  useEffect(() => {
    if (!existingFileName || !lenderId || !state) return;
    service
      .getStateLicenseData(lenderId, type, state, loanDetails?.lenderOriginatorId)
      .then(response => {
        const data = response?.data?.data;
        if (data?.expirationDate) setExpirationDate(new Date(data.expirationDate));
        if (data?.originatorFullName) setOriginator(data.originatorFullName);
      })
      .catch(() => {});
  }, [lenderId, type, state]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Closes the surrounding task dialog by triggering its built-in Escape-to-close.
  const closeTaskDialog = () => {
    containerRef.current?.dispatchEvent(
      new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', bubbles: true })
    );
  };

  const submit = async () => {
    const date = expirationDate ? new Date(expirationDate) : null;
    if(isBlank(originator) && type == 'ORIGINATOR'){
      globalStore.notificationStore.showErrorNotification({
        message: 'The loan originator must be selected in the Loan Summary tab before submitting.',
      });
      return;
    }
    if (!date || !isValid(date)) {
      setExpirationError('Expiration date is required.');
      return;
    }
    setExpirationError(null);
    if (!uploadItems?.[0]?.file) {
      globalStore.notificationStore.showErrorNotification({
        message: 'Please upload a license document before submitting.',
      });
      return;
    }

    try {
      setIsSubmitting(true);

      // taskv2 derives lender/type/state/originator from the loan, uploads the
      // document for the loan's lender, saves the license, attaches it to the task
      // and moves the task to Being Reviewed.
      await service.submitStateLicense(taskId, format(date, 'MM/dd/yyyy'), uploadItems[0].file);

      globalStore.notificationStore.showSuccessNotification({
        message: 'License submitted successfully and is now being reviewed.',
      });
      onSubmitted?.();
      closeTaskDialog();
    } catch (e: any) {
      const message = e?.response?.data?.error?.message
        || e?.response?.data?.message
        || 'Error submitting license.';
      globalStore.notificationStore.showErrorNotification({ message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Grid ref={containerRef} container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="body2" color="textSecondary">
          {existingFileName
            ? 'A state license has already been submitted. You may re-submit below to update it.'
            : 'A state license is required for this loan but has not been submitted. Upload the license document below to resolve this closing condition.'}
        </Typography>
      </Grid>

      <LockedField label="Type" value={typeLabel(type)} />

      {type == 'ORIGINATOR' &&
        <>
          {isBlank(originator) &&
            <Grid item xs={12}>
              <Alert severity='error'>
                <AlertTitle>Loan Originator</AlertTitle>
                <div>Please select a originator for the loan in the Loan Summary tab.</div>
              </Alert>
            </Grid>
          }

          <LockedField label="Originator" value={originator} />
        </>
      }

      <LockedField label="State" value={state} />

      <Grid item xs={12}>
        <Typography variant="body1" className="x-field-label">
          
        </Typography>
        <DateField
          label="Expiration Date"
          required
          value={expirationDate}
          onChange={date => setExpirationDate(date)}
          error={!!expirationError}
          helperText={expirationError}
          testId="stateLicenseExpirationDate"
          disablePast
          inputVariant="outlined"
          fullWidth
          format="MM/dd/yyyy"
          disabled={!!existingFileName}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom>
          Document *
        </Typography>
        {existingFileName ? (
          <FileUploadDownloadArea
            file={null}
            onChange={() => {}}
            readOnlyFileName={existingFileName}
            onDownload={onDownloadExisting}
            disableDownload={!onDownloadExisting}
          />
        ) : (
          <_StateLicenseUpload
            items={uploadItems}
            setItems={setUploadItems}
            onRemoveItem={() => setUploadItems([])}
          />
        )}
      </Grid>

      <Grid item xs={12} container justifyContent='flex-end' spacing={0}>
        <Grid item>
          <Button
            testId="submitStateLicense"
            variant="contained"
            color="primary"
            onClick={submit}
            disabled={isSubmitting || !!existingFileName}
          >
            Submit License
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
});
